import { useEffect, useRef, useState } from "react";
import type { ApiError, VaultStatus } from "@gooner/shared";
import { WALL_UPLOAD_TYPES } from "@gooner/shared";
import { apiErrorOf, userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { trackFapsInsufficientShown, useFaps } from "../../lib/faps";
import { trackGoldGate } from "../../lib/gold";
import { trackVaultExpandClicked, trackVaultOpened, trackVaultQuotaHit, trackVaultUploadStarted } from "../../lib/vault";
import { fmtBytes } from "../lib/format";
import { FapsAmount } from "../ui/FapsAmount";
import { Button, cx } from "../ui/primitives";
import { InsufficientFaps } from "../../components/InsufficientFaps";
import { FapsDialog } from "./FapsDialog";
import { GoldDialog } from "./GoldDialog";
import { MASK } from "../../lib/replayMask";

/**
 * The head of "Your Uploads": how full the vault is, the door to put a file in, and the
 * door to buy more room.
 *
 * Three refusals, three answers, none of them a dead end. The vault is FULL (409
 * vaultFull): the same panel offers the next step of space — or, for a Basic account
 * already at Basic's ceiling, the Premium dialog, because Premium's ceiling is the one
 * thing that lifts it. The balance is SHORT for that step (409 needed): the wallet's own
 * footer, chest and Premium, the wall's exact doors. A file is too big or the wrong kind
 * (413/415): the server's sentence, which already says the limit.
 *
 * Everything here is quoted from VaultStatus, never a literal: the operator sets these
 * numbers, and a panel that named one size while the setting said another would be the
 * lie the wallet dialog was built to avoid.
 *
 * NO preview, no rung, no tags, by rule: an upload plays as its master. The one derived
 * file is its poster, a first frame cut at intake (2026-09-12), so a tile here shows a
 * picture; an upload from before that falls back to ClipTile's first-frame <video>.
 *
 * Multi-select (2026-09-28): the picker takes many files in one action; each still goes
 * through the same one-file POST /uploads. Sequential so quota math and the panel's
 * status stay honest file-by-file. A vaultFull mid-batch stops the rest — more of the
 * same would only refuse the same way.
 */
type FileJob = {
  name: string;
  status: "pending" | "sending" | "done" | "existing" | "error" | "skipped";
  error?: string;
};

export function VaultPanel({
  vault,
  onChanged,
}: {
  vault: VaultStatus;
  /** The server's fresh status after an upload, expand or delete — the caller reloads. */
  onChanged: (next: VaultStatus) => void;
}) {
  const user = useSession((s) => s.user);
  const wallet = useFaps();
  const [uploading, setUploading] = useState(false);
  const [attested, setAttested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Success with nothing new to show: the same bytes were already in the vault.
  const [note, setNote] = useState<string | null>(null);
  const [jobs, setJobs] = useState<FileJob[] | null>(null);
  const [full, setFull] = useState(false);
  const [short, setShort] = useState<number | null>(null);
  const [goldOpen, setGoldOpen] = useState(false);
  const [fapsOpen, setFapsOpen] = useState<"chest" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const capacity = vault.baseBytes + vault.extraBytes;
  const atCeiling = capacity >= vault.maxBytes;
  const premium = (user?.goldUntil ?? 0) > Date.now();
  // The impression, with the fill level of whoever is looking. Once per page load
  // (the wrapper dedupes), so a status refresh after an upload is not a second look.
  useEffect(() => {
    trackVaultOpened(vault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patchJob(i: number, patch: Partial<FileJob>) {
    setJobs((cur) => (cur ? cur.map((j, idx) => (idx === i ? { ...j, ...patch } : j)) : cur));
  }

  async function sendMany(files: File[]) {
    if (files.length === 0) return;
    setError(null);
    setNote(null);
    setFull(false);
    setUploading(true);
    setJobs(files.map((f) => ({ name: f.name, status: "pending" as const })));
    let stoppedForFull = false;
    let existingCount = 0;
    let okCount = 0;
    let failCount = 0;
    let lastFailMsg: string | null = null;

    try {
      for (let i = 0; i < files.length; i++) {
        if (stoppedForFull) {
          patchJob(i, { status: "skipped", error: "Vault full — not sent." });
          continue;
        }
        const file = files[i]!;
        patchJob(i, { status: "sending" });
        try {
          trackVaultUploadStarted(file.size);
          const r = await userApi.vaultUpload(file);
          onChanged(r.vault);
          if (r.existing) {
            existingCount += 1;
            patchJob(i, { status: "existing" });
          } else {
            okCount += 1;
            patchJob(i, { status: "done" });
          }
        } catch (err) {
          const e = apiErrorOf(err);
          const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
          if (e?.vaultFull) {
            trackVaultQuotaHit();
            setFull(true);
            stoppedForFull = true;
            lastFailMsg = msg || "Your vault is full.";
            patchJob(i, { status: "error", error: lastFailMsg });
            failCount += 1;
            continue;
          }
          lastFailMsg = msg || "That upload didn't go through.";
          patchJob(i, { status: "error", error: lastFailMsg });
          failCount += 1;
        }
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }

    if (files.length === 1 && failCount === 1) {
      // Same top-line refusal the panel always showed for one file.
      setError(lastFailMsg || "That upload didn't go through.");
    } else if (failCount > 0 || stoppedForFull) {
      setError(
        stoppedForFull
          ? "Your vault filled up mid-batch — expand or free space, then retry what was skipped."
          : `${failCount} of ${files.length} didn't go through.`,
      );
    } else if (existingCount > 0 && okCount === 0) {
      setNote(
        existingCount === 1
          ? "Already in your vault."
          : `All ${existingCount} were already in your vault.`,
      );
    } else if (existingCount > 0) {
      setNote(`${okCount} added · ${existingCount} already in your vault.`);
    } else if (okCount > 1) {
      setNote(`${okCount} added to your vault.`);
    }
  }

  async function expand() {
    trackVaultExpandClicked();
    setError(null);
    setShort(null);
    // Pre-empt locally the way the wall does: the 409 is always the backstop and carries
    // the same number, but a form that asks the server to say "you're short" when the
    // wallet already knows is a round trip spent on a sentence.
    if (wallet.balance !== null && wallet.balance < vault.stepPriceFaps) {
      const needed = vault.stepPriceFaps - wallet.balance;
      trackFapsInsufficientShown(needed, "vault");
      setShort(needed);
      return;
    }
    try {
      const r = await userApi.vaultExpand();
      setFull(false);
      onChanged(r.vault);
      void wallet.refresh();
    } catch (err) {
      const e = apiErrorOf(err) as (ApiError & { needed?: number }) | undefined;
      // The typed flag FIRST: Premium would lift this refusal, and the answer is the
      // Premium dialog, not a sentence.
      if (e?.goldUpsell) {
        // The gate hit is the Gold funnel's first step; the dialog reports the second
        // (gold_upsell_shown{vault}) on its own mount.
        trackGoldGate("vault");
        setGoldOpen(true);
        return;
      }
      if (typeof e?.needed === "number") {
        trackFapsInsufficientShown(e.needed, "vault");
        setShort(e.needed);
        return;
      }
      const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
      setError(msg || "That didn't go through.");
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] p-3">
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-medium text-zinc-300">Vault space</span>
          <span className="text-[11px] text-zinc-500">
            {vault.items} upload{vault.items === 1 ? "" : "s"}
          </span>
        </div>
        <BytesMeter used={vault.usedBytes} cap={capacity} full={full} />
      </div>

      <div className="rounded-lg border border-[var(--color-edge)] p-3">
        <label className="mb-2 flex items-start gap-2 text-[11px] leading-snug text-zinc-400">
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.currentTarget.checked)}
            className="mt-0.5"
          />
          <span>
            I&rsquo;m 18+, this is mine to upload, and I hold any consents and records the
            law requires.
          </span>
        </label>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={Object.keys(WALL_UPLOAD_TYPES).join(",")}
          disabled={uploading || !attested}
          onChange={(e) => {
            const list = e.currentTarget.files;
            if (!list || list.length === 0) return;
            void sendMany(Array.from(list));
          }}
          className="block w-full text-xs text-zinc-400 file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--color-accent)] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-black hover:file:opacity-90 disabled:opacity-50"
        />
        <p className="mt-2 text-[11px] leading-snug text-zinc-500">
          MP4 or WebM video up to {vault.maxSeconds}s, or a JPG, PNG or WebP image. Under{" "}
          {fmtBytes(vault.maxFileBytes)}. Pick one or many — each plays as uploaded,
          nothing is re-encoded, tagged or framed.
        </p>
        {jobs && jobs.length > 0 ? (
          <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-[11px]">
            {jobs.map((j, i) => (
              <li
                key={`${i}-${j.name}`}
                className={cx(
                  "flex gap-2 truncate",
                  j.status === "error" || j.status === "skipped"
                    ? "text-red-400"
                    : j.status === "sending"
                      ? "text-zinc-300"
                      : "text-zinc-400",
                )}
              >
                <span className="shrink-0 tabular-nums">
                  {j.status === "pending"
                    ? "·"
                    : j.status === "sending"
                      ? "…"
                      : j.status === "done"
                        ? "✓"
                        : j.status === "existing"
                          ? "="
                          : j.status === "skipped"
                            ? "–"
                            : "✗"}
                </span>
                {/* An upload's filename is the person's (and often identifying): masked in
                    replay, with the hover title that repeats it on the same masked span.
                    The status glyph and the error stay readable. */}
                <span className="min-w-0 truncate" title={j.error ? `${j.name}: ${j.error}` : j.name} {...MASK}>
                  {j.name}
                </span>
                {j.error ? <span className="shrink-0 truncate opacity-80">{j.error}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        {uploading && (!jobs || jobs.length === 0) ? (
          <p className="mt-2 text-xs text-zinc-400">Sending…</p>
        ) : null}
        {!uploading && error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
        {!uploading && !error && note ? <p className="mt-2 text-xs text-zinc-400">{note}</p> : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {atCeiling ? (
          premium ? (
            <p className="text-xs text-zinc-500">Your vault is at its ceiling.</p>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setGoldOpen(true)}>
              At Basic&rsquo;s ceiling — Premium goes further
            </Button>
          )
        ) : (
          <Button size="sm" onClick={() => void expand()}>
            Expand +{fmtBytes(vault.stepBytes)} for&nbsp;
            <FapsAmount n={vault.stepPriceFaps} size={11} tone="ink" />
          </Button>
        )}
        {full ? (
          <p className="text-xs text-[#f5c518]">Your vault is full.</p>
        ) : null}
      </div>
      {short !== null ? (
        <InsufficientFaps
          needed={short}
          chestReady={wallet.chestReady}
          onDoor={(door) => {
            if (door === "premium") setGoldOpen(true);
            else setFapsOpen("chest");
          }}
        />
      ) : null}

      {fapsOpen ? <FapsDialog source="vault_short" focus={fapsOpen} onClose={() => setFapsOpen(null)} /> : null}
      {goldOpen ? <GoldDialog source="vault" onClose={() => setGoldOpen(false)} /> : null}
    </div>
  );
}

/** The wallet's Meter, in bytes: used against capacity, gold when full. */
function BytesMeter({ used, cap, full }: { used: number; cap: number; full: boolean }) {
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const isFull = full || (cap > 0 && used >= cap);
  return (
    <div className="mt-1">
      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-panel-2)]">
        <div
          className={cx(
            "h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500 motion-safe:ease-out",
            isFull ? "bg-[#f5c518]" : "bg-[var(--color-accent)]",
          )}
          style={{ width: `${used > 0 ? Math.max(2, pct) : 0}%` }}
        />
      </div>
      <div className="mt-0.5 text-[11px] tabular-nums text-zinc-500">
        {fmtBytes(used)} / {fmtBytes(cap)}
        {isFull ? " — full" : ""}
      </div>
    </div>
  );
}
